import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Progress,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloudDownloadOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons';
import { del, get, post } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import './ResourcePullPage.css';

const { Text } = Typography;

interface PullTask {
  id: number;
  source_id: string;
  source_name: string;
  requested_count: number;
  interval_seconds?: number;
  max_chapters?: number;
  truncate_excess?: boolean;
  channel?: '随机' | '男频' | '女频';
  category?: string;
  status: 'pending' | 'running' | 'completed' | 'partial' | 'failed' | 'cancelled';
  cancel_requested?: boolean;
  message: string;
  scanned_count: number;
  imported_count: number;
  skipped_count: number;
  filtered_count?: number;
  failed_count: number;
  current_title?: string;
  processed_chapters?: number;
  total_chapters?: number;
  progress_percent?: number;
  current_stage?: string;
  retry_count?: number;
  retry_limit?: number;
  last_error?: string;
  next_retry_at?: string;
  uploaded_chapters?: number;
  upload_total_chapters?: number;
  recovery_count?: number;
  errors?: string[];
  created_at: string;
  updated_at?: string;
  finished_at?: string;
}

interface ResourcePlatform {
  key: string;
  name: string;
  description: string;
  rights_note: string;
}

interface LibraryCategory {
  id: string;
  channel: '男频' | '女频';
  name: string;
  enabled?: boolean;
  sort?: number;
}

interface ImportRecord {
  id: number;
  source_name: string;
  external_id: string;
  project_id?: number;
  title: string;
  author: string;
  reader?: string;
  category?: string;
  chapter_count?: number;
  word_count?: number;
  cover_url?: string;
  status: 'imported' | 'retrying' | 'failed';
  message: string;
  retry_count?: number;
  retry_limit?: number;
  last_error?: string;
  created_at: string;
  updated_at?: string;
}

interface ListResponse<T> {
  data: T[];
  total: number;
}

interface SearchResult {
  external_id: string;
  title: string;
  author: string;
  description: string;
  cover_url: string;
  source_url: string;
}

interface SearchResponse {
  data: SearchResult[];
  source: string;
}

const dateText = (value?: string) => (value ? new Date(value).toLocaleString() : '-');

const taskStatus: Record<PullTask['status'], { color: string; label: string }> = {
  pending: { color: 'default', label: '等待中' },
  running: { color: 'processing', label: '拉取中' },
  completed: { color: 'success', label: '已完成' },
  partial: { color: 'warning', label: '部分完成' },
  failed: { color: 'error', label: '失败' },
  cancelled: { color: 'default', label: '已终止' },
};

const pullStageLabel: Record<string, string> = {
  discovering: '正在寻找资源',
  discovery_retry: '寻找资源重试中',
  discovery_wait: '等待继续寻找',
  source_retry: '作品信息重试中',
  book_retry: '整本重新抓取中',
  chapters: '正在抓取章节',
  chapter_retry: '章节重试中',
  upload_cover: '正在上传封面',
  upload_chapters: '正在上传章节',
  publish: '正在上架',
  publish_retry: '上架重试中',
  worker_retry: '任务自动恢复中',
  recovering: '服务重启恢复中',
  interval_wait: '等待处理下一本',
};

const ResourcePullPage: React.FC = () => {
  const [platforms, setPlatforms] = useState<ResourcePlatform[]>([]);
  const [categories, setCategories] = useState<LibraryCategory[]>([]);
  const [tasks, setTasks] = useState<PullTask[]>([]);
  const [records, setRecords] = useState<ImportRecord[]>([]);
  const [keyword, setKeyword] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('shuhaige');
  const [pullCount, setPullCount] = useState(5);
  const [intervalSeconds, setIntervalSeconds] = useState(3);
  const [maxChapters, setMaxChapters] = useState(500);
  const [truncateExcess, setTruncateExcess] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<'随机' | '男频' | '女频'>('随机');
  const [selectedCategory, setSelectedCategory] = useState('随机');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [crawlingId, setCrawlingId] = useState('');
  const [batchPulling, setBatchPulling] = useState(false);
  const [clearingTasks, setClearingTasks] = useState(false);
  const [clearingRecords, setClearingRecords] = useState(false);
  const [cancellingTaskId, setCancellingTaskId] = useState<number | null>(null);

  const loadData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [platformRows, categoryRows, taskRows, recordRows] = await Promise.all([
        get<ResourcePlatform[]>(API_ENDPOINTS.resourcePull.platforms),
        get<ListResponse<LibraryCategory>>(API_ENDPOINTS.resourcePull.categories),
        get<ListResponse<PullTask>>(API_ENDPOINTS.resourcePull.tasks),
        get<ListResponse<ImportRecord>>(API_ENDPOINTS.resourcePull.records),
      ]);
      setPlatforms(platformRows || []);
      setCategories(categoryRows.data || []);
      setTasks(taskRows.data || []);
      setRecords(recordRows.data || []);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载资源拉取数据失败');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const hasActiveTask = tasks.some((task) => task.status === 'pending' || task.status === 'running');
  useEffect(() => {
    if (!hasActiveTask) return undefined;
    const timer = window.setInterval(() => loadData(false), 3000);
    return () => window.clearInterval(timer);
  }, [hasActiveTask]);

  const metrics = useMemo(() => ({
    imported: records.filter((record) => record.status === 'imported').length,
    failed: records.filter((record) => record.status === 'failed').length,
  }), [records]);
  const categoryOptions = useMemo(
    () => categories
      .filter((item) => selectedChannel === '随机' || item.channel === selectedChannel)
      .map((item) => ({
        value: item.name,
        label: selectedChannel === '随机' ? `${item.channel} · ${item.name}` : item.name,
      })),
    [categories, selectedChannel],
  );

  const changeChannel = (value: '随机' | '男频' | '女频') => {
    setSelectedChannel(value);
    setSelectedCategory('随机');
  };

  const searchBooks = async () => {
    const value = keyword.trim();
    if (!value) {
      message.warning('请输入小说名称');
      return;
    }
    setSearching(true);
    try {
      const response = await get<SearchResponse>(
        `${API_ENDPOINTS.resourcePull.shuhaigeSearch}?keyword=${encodeURIComponent(value)}`,
      );
      setResults(response.data || []);
      if (!response.data?.length) message.info('书海阁没有找到相关小说');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '搜索书海阁失败');
    } finally {
      setSearching(false);
    }
  };

  const crawlBook = async (book: SearchResult) => {
    setCrawlingId(book.external_id);
    try {
      await post(API_ENDPOINTS.resourcePull.shuhaigeCrawl, {
        source_url: book.source_url,
        title: book.title,
        max_chapters: maxChapters,
        truncate_excess: truncateExcess,
        channel: selectedChannel,
        category: selectedCategory,
      });
      message.success(`《${book.title}》已进入抓取队列`);
      await loadData(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '创建抓取任务失败');
    } finally {
      setCrawlingId('');
    }
  };

  const startBatchPull = async () => {
    if (!selectedPlatform) {
      message.warning('请选择资源平台');
      return;
    }
    setBatchPulling(true);
    try {
      await post(API_ENDPOINTS.resourcePull.pull, {
        platform: selectedPlatform,
        count: pullCount,
        interval_seconds: intervalSeconds,
        max_chapters: maxChapters,
        truncate_excess: truncateExcess,
        channel: selectedChannel,
        category: selectedCategory,
      });
      message.success(`批量抓取任务已创建，本次目标新增 ${pullCount} 本`);
      await loadData(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '创建批量抓取任务失败');
    } finally {
      setBatchPulling(false);
    }
  };

  const clearImportRecords = async () => {
    setClearingRecords(true);
    try {
      const response = await del<{ message: string; deleted_count: number }>(
        API_ENDPOINTS.resourcePull.records,
      );
      setRecords([]);
      message.success(`已清除 ${response.deleted_count} 条入库记录`);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '清除入库记录失败');
    } finally {
      setClearingRecords(false);
    }
  };

  const clearPullTasks = async () => {
    setClearingTasks(true);
    try {
      const response = await del<{ message: string; deleted_count: number; active_count: number }>(
        API_ENDPOINTS.resourcePull.tasks,
      );
      setTasks((items) => items.filter((task) => task.status === 'pending' || task.status === 'running'));
      message.success(
        response.active_count
          ? `已清除 ${response.deleted_count} 条历史任务，保留 ${response.active_count} 条执行中任务`
          : `已清除 ${response.deleted_count} 条抓取任务记录`,
      );
    } catch (error) {
      message.error(error instanceof Error ? error.message : '清除抓取任务记录失败');
    } finally {
      setClearingTasks(false);
    }
  };

  const cancelPullTask = async (task: PullTask) => {
    setCancellingTaskId(task.id);
    try {
      await post(API_ENDPOINTS.resourcePull.cancelTask(task.id), {});
      message.success(`已通知终止任务 #${task.id}`);
      await loadData(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '终止抓取任务失败');
    } finally {
      setCancellingTaskId(null);
    }
  };

  const searchColumns: ColumnsType<SearchResult> = [
    {
      title: '搜索结果',
      dataIndex: 'title',
      render: (_, record) => (
        <div className="pull-book-cell">
          {record.cover_url
            ? <Image src={record.cover_url} width={48} height={66} preview={false} />
            : <div className="pull-cover-empty" />}
          <div>
            <strong>{record.title}</strong>
            <Text type="secondary">{record.author || '未知作者'}</Text>
          </div>
        </div>
      ),
    },
    { title: '简介', dataIndex: 'description', ellipsis: true },
    {
      title: '操作',
      width: 130,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<CloudDownloadOutlined />}
          loading={crawlingId === record.external_id}
          disabled={hasActiveTask}
          onClick={() => crawlBook(record)}
        >
          抓取入库
        </Button>
      ),
    },
  ];

  const taskColumns: ColumnsType<PullTask> = [
    {
      title: '任务 / 当前小说',
      width: 260,
      render: (_, task) => (
        <div className="pull-task-title">
          <strong>#{task.id}{task.current_title ? ` · 《${task.current_title}》` : ''}</strong>
          <Text type="secondary">
            {task.current_stage === 'upload_chapters' && task.upload_total_chapters
              ? `上传章节 ${task.uploaded_chapters || 0}/${task.upload_total_chapters}`
              : task.total_chapters
              ? `章节 ${task.processed_chapters || 0}/${task.total_chapters}`
              : pullStageLabel[task.current_stage || ''] || '正在读取作品信息'}
          </Text>
          {Boolean(task.retry_count) && (
            <Text type="warning">
              第 {task.retry_count} 次重试
              {task.retry_limit ? ` / 最多 ${task.retry_limit} 次` : ' · 持续自动重试'}
            </Text>
          )}
          <Text type="secondary">
            入库分类：{task.channel || '随机'} / {task.category || '随机'}
          </Text>
        </div>
      ),
    },
    { title: '资源平台', dataIndex: 'source_name', width: 220 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (value: PullTask['status']) => <Tag color={taskStatus[value]?.color}>{taskStatus[value]?.label || value}</Tag>,
    },
    {
      title: '进度',
      width: 250,
      render: (_, task) => {
        const percent = Math.min(
          100,
          task.progress_percent
            ?? (task.requested_count ? Math.round(task.imported_count / task.requested_count * 100) : 0),
        );
        return (
          <div className="pull-task-progress">
            <Progress percent={percent} size="small" status={task.status === 'failed' ? 'exception' : undefined} />
            <Text type="secondary">
              新增 {task.imported_count}/{task.requested_count} · 跳过已有 {task.skipped_count}
              {' · '}超限 {task.filtered_count || 0} · 失败 {task.failed_count}
            </Text>
          </div>
        );
      },
    },
    { title: '结果', dataIndex: 'message', ellipsis: true },
    { title: '创建时间', dataIndex: 'created_at', width: 180, render: dateText },
    {
      title: '操作',
      width: 110,
      fixed: 'right',
      render: (_, task) => task.status === 'pending' || task.status === 'running' ? (
        <Popconfirm
          title={`终止任务 #${task.id}？`}
          description="会停止继续扫描和抓取；已经完整入库的小说会保留。"
          okText="终止任务"
          cancelText="取消"
          okButtonProps={{ danger: true }}
          onConfirm={() => cancelPullTask(task)}
        >
          <Button
            danger
            size="small"
            icon={<StopOutlined />}
            loading={cancellingTaskId === task.id}
            disabled={Boolean(task.cancel_requested)}
          >
            {task.cancel_requested ? '终止中' : '终止'}
          </Button>
        </Popconfirm>
      ) : null,
    },
  ];

  const recordColumns: ColumnsType<ImportRecord> = [
    {
      title: '作品',
      dataIndex: 'title',
      width: 300,
      render: (_, record) => (
        <div className="pull-book-cell">
          {record.cover_url
            ? <Image src={record.cover_url} width={42} height={58} preview={false} />
            : <div className="pull-cover-empty" />}
          <div>
            <strong>{record.title || record.external_id || '未知作品'}</strong>
            <Text type="secondary">{record.author || '未知作者'}</Text>
          </div>
        </div>
      ),
    },
    { title: '来源', dataIndex: 'source_name', width: 210 },
    {
      title: '频道 / 分类',
      width: 180,
      render: (_, record) => record.status === 'imported'
        ? `${record.reader || '-'} / ${record.category || '-'}`
        : '-',
    },
    {
      title: '入库内容',
      width: 190,
      render: (_, record) => record.status === 'imported'
        ? `${record.chapter_count || 0} 章 / ${(record.word_count || 0).toLocaleString()} 字`
        : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value) => {
        if (value === 'imported') return <Tag color="success">已上架</Tag>;
        if (value === 'retrying') return <Tag color="processing">重试中</Tag>;
        return <Tag color="error">失败</Tag>;
      },
    },
    { title: '结果', dataIndex: 'message', ellipsis: true },
    { title: '项目 ID', dataIndex: 'project_id', width: 100, render: (value) => value || '-' },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      width: 180,
      render: (value, record) => dateText(value || record.created_at),
    },
  ];

  return (
    <div className="resource-pull-page">
      <div className="pull-page-header">
        <div>
          <h1 className="page-title">资源抓取</h1>
          <Text type="secondary">搜索书海阁小说，按章节上限筛选后将正文存入对象存储并自动上架</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => loadData()}>刷新</Button>
      </div>

      <Collapse
        className="pull-search-collapse"
        items={[{
          key: 'precise-search',
          label: (
            <div className="pull-search-collapse-title">
              <SearchOutlined />
              <span>按书名精准搜索</span>
              <Text type="secondary">辅助功能，点击展开</Text>
            </div>
          ),
          children: (
            <>
              <div className="pull-search-row">
                <Input
                  size="large"
                  prefix={<SearchOutlined />}
                  value={keyword}
                  placeholder="输入小说名称，例如：斗破苍穹"
                  allowClear
                  onChange={(event) => setKeyword(event.target.value)}
                  onPressEnter={searchBooks}
                />
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  loading={searching}
                  onClick={searchBooks}
                >
                  搜索小说
                </Button>
              </div>
              <Text type="secondary">
                搜索后选择正确的作者和作品，章节上限及入库分类与下方批量抓取共用。
              </Text>
              {(results.length > 0 || searching) && (
                <div className="pull-inline-search-results">
                  <div className="pull-section-title">
                    <h2>搜索结果</h2>
                    <Tag color="blue">{results.length} 本</Tag>
                  </div>
                  <Table
                    rowKey="external_id"
                    columns={searchColumns}
                    dataSource={results}
                    loading={searching}
                    pagination={{ pageSize: 8, hideOnSinglePage: true }}
                    scroll={{ x: 880 }}
                  />
                </div>
              )}
            </>
          ),
        }]}
      />

      <section className="pull-action-panel pull-classification-panel">
        <div className="pull-section-title">
          <div>
            <h2>入库频道与分类</h2>
            <Text type="secondary">搜索抓取和自动批量抓取共用；默认每本书随机分配到一个启用分类</Text>
          </div>
        </div>
        <div className="pull-classification-controls">
          <div className="pull-field">
            <Text strong>频道</Text>
            <Select
              value={selectedChannel}
              onChange={changeChannel}
              options={[
                { value: '随机', label: '随机（男频 / 女频）' },
                { value: '男频', label: '男频' },
                { value: '女频', label: '女频' },
              ]}
            />
          </div>
          <div className="pull-field">
            <Text strong>具体分类</Text>
            <Select
              showSearch
              value={selectedCategory}
              onChange={setSelectedCategory}
              options={[
                { value: '随机', label: selectedChannel === '随机' ? '随机（全部分类）' : `随机（${selectedChannel}分类）` },
                ...categoryOptions,
              ]}
            />
          </div>
          <Alert
            type="success"
            showIcon
            message={selectedCategory === '随机'
              ? `${selectedChannel === '随机' ? '男频、女频及其分类' : `${selectedChannel}下的分类`}将逐本随机分配`
              : `新抓取作品将归入：${selectedChannel === '随机' ? '按分类确定频道' : selectedChannel} / ${selectedCategory}`}
          />
        </div>
      </section>

      <section className="pull-action-panel pull-batch-panel">
        <div className="pull-section-title">
          <div>
            <h2>自动批量抓取</h2>
            <Text type="secondary">不输入书名，随机选择作品；已入库小说自动跳过且不占新增数量</Text>
          </div>
        </div>
        <div className="pull-action-controls">
          <div className="pull-field pull-platform-field">
            <Text strong>资源平台</Text>
            <Select
              value={selectedPlatform}
              options={platforms.map((platform) => ({ value: platform.key, label: platform.name }))}
              onChange={setSelectedPlatform}
            />
          </div>
          <div className="pull-field">
            <Text strong>本次新增数量</Text>
            <InputNumber
              min={1}
              max={200}
              value={pullCount}
              onChange={(value) => setPullCount(value || 1)}
            />
            <Text type="secondary">单次最多 200 本</Text>
          </div>
          <div className="pull-field">
            <Text strong>最大章节数</Text>
            <InputNumber
              min={2}
              max={1000}
              value={maxChapters}
              addonAfter="章"
              onChange={(value) => setMaxChapters(value || 500)}
            />
            <Text type="secondary">
              {truncateExcess ? `超过上限时只抓取前 ${maxChapters} 章` : '超过上限的整本跳过'}
            </Text>
          </div>
          <div className="pull-field">
            <Text strong>每本间隔</Text>
            <InputNumber
              min={0}
              max={30}
              value={intervalSeconds}
              addonAfter="秒"
              onChange={(value) => setIntervalSeconds(value ?? 3)}
            />
            <Text type="secondary">建议 3–5 秒，0 为不停顿</Text>
          </div>
          <div className="pull-field pull-truncate-field">
            <Text strong>超限时截取</Text>
            <div className="pull-switch-control">
              <Switch checked={truncateExcess} onChange={setTruncateExcess} />
              <span>{truncateExcess ? '只取前段' : '整本跳过'}</span>
            </div>
            <Text type="secondary">开启后，几千章的小说也只抓取设定的前 N 章</Text>
          </div>
          <Button
            type="primary"
            size="large"
            icon={<CloudDownloadOutlined />}
            disabled={hasActiveTask}
            loading={batchPulling}
            onClick={startBatchPull}
          >
            {hasActiveTask ? '已有任务执行中' : '开始随机抓取'}
          </Button>
        </div>
      </section>

      <section className="pull-section">
        <div className="pull-section-title">
          <h2>抓取任务</h2>
          <Space size={16}>
            <Popconfirm
              title="清除已结束的抓取任务？"
              description="只清除已完成、部分完成和失败的任务记录，不会删除执行中任务或已入库小说。"
              okText="确认清除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={clearPullTasks}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={clearingTasks}
                disabled={!tasks.some((task) => task.status !== 'pending' && task.status !== 'running')}
              >
                清除记录
              </Button>
            </Popconfirm>
            {hasActiveTask && <Tag color="processing">自动刷新中</Tag>}
          </Space>
        </div>
        <Table
          rowKey="id"
          columns={taskColumns}
          dataSource={tasks}
          loading={loading}
          pagination={{ pageSize: 6, hideOnSinglePage: true }}
          scroll={{ x: 980 }}
          locale={{ emptyText: '暂无抓取任务' }}
        />
      </section>

      <section className="pull-section">
        <div className="pull-section-title">
          <h2>入库记录</h2>
          <Space size={16}>
            <Popconfirm
              title="清除全部入库记录？"
              description="只清除抓取历史，不会删除已经入库的小说、章节和封面。"
              okText="确认清除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={clearImportRecords}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={clearingRecords}
                disabled={!records.length}
              >
                清除记录
              </Button>
            </Popconfirm>
            <Text type="secondary">成功 {metrics.imported} 本 / 失败 {metrics.failed} 本</Text>
          </Space>
        </div>
        <Table
          rowKey="id"
          columns={recordColumns}
          dataSource={records}
          loading={loading}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          scroll={{ x: 1180 }}
          locale={{ emptyText: '暂无入库记录' }}
        />
      </section>
    </div>
  );
};

export default ResourcePullPage;
